import React from 'react';
import { getResponsiveMultiplier } from '../../utils/visualizerUtils';

// Global state to track ice buildup and frozen bars
let iceBuildup = new Map(); // Track ice accumulation on each bar
let frozenBars = new Map(); // Track which bars are frozen and their break timers
let lastTime = 0;

export function drawFrostyWaves(ctx, dataArray, width, height, time) {
  if (!ctx || !dataArray || width <= 0 || height <= 0) return;
  
  // Initialize ice tracking if needed
  if (iceBuildup.size === 0) {
    for (let i = 0; i < dataArray.length; i++) {
      iceBuildup.set(i, 0);
      frozenBars.set(i, { frozen: false, breakTimer: 0, freezeTimer: 0 });
    }
  }
  
  const deltaTime = time - lastTime;
  lastTime = time;
  
  // Frosty ice background gradient
  const bgGradient = ctx.createLinearGradient(0, 0, 0, height);
  bgGradient.addColorStop(0, 'hsla(200, 80%, 20%, 0.3)');
  bgGradient.addColorStop(1, 'hsla(220, 90%, 10%, 0.5)');
  ctx.fillStyle = bgGradient;
  ctx.fillRect(0, 0, width, height);
  
  // Optimize bar count for better performance (reduced from full array)
  const maxBars = Math.min(180, dataArray.length); // Increased from 96 to 180 (30% reduction)
  const step = Math.floor(dataArray.length / maxBars);
  const barWidth = width / maxBars * getResponsiveMultiplier();
  let x = 0;
  
  // Draw bars using optimized approach with reduced bar count
  for (let i = 0; i < maxBars; i++) {
    const dataIndex = Math.min(i * step, dataArray.length - 1);
    const intensity = dataArray[dataIndex] / 255;
    const barState = frozenBars.get(dataIndex);
    
    // Update ice buildup and frozen state
    updateBarIceState(dataIndex, intensity, deltaTime, barState);
    
    // Wave effect with time-based animation (reduced when frozen)
    const waveMultiplier = barState.frozen ? 0.2 : 1.0;
    const waveOffset = Math.sin(time * 2 + dataIndex * 0.1) * 20 * waveMultiplier;
    const baseBarHeight = intensity * height * 0.8 + waveOffset;
    
    // Add ice buildup to bar height
    const currentIceBuildup = iceBuildup.get(dataIndex);
    const barHeight = Math.floor(baseBarHeight + currentIceBuildup);
    
    const barY = height - Math.max(barHeight, 10);
    
    // Frosty ice-like gradient (different colors when frozen)
    const baseHue = 180 + (dataIndex / dataArray.length) * 60; // Blue to cyan range
    const hue = barState.frozen ? baseHue - 30 : baseHue; // Darker blue when frozen
    const saturation = barState.frozen ? 100 : 80; // More saturated when frozen
    
    const gradient = ctx.createLinearGradient(0, barY, 0, height);
    gradient.addColorStop(0, `hsl(${hue}, ${saturation}%, 80%)`);
    gradient.addColorStop(0.5, `hsl(${hue}, ${saturation - 10}%, 60%)`);
    gradient.addColorStop(1, `hsl(${hue}, ${saturation - 20}%, 40%)`);
    
    ctx.fillStyle = gradient;
    ctx.fillRect(x, barY, barWidth, Math.max(barHeight, 10));
    
    // Draw ice buildup layers on top
    drawIceBuildup(ctx, x, barY, barWidth, currentIceBuildup, hue, time, i);
    
    // Draw frozen effect if bar is frozen
    if (barState.frozen) {
      drawFrozenEffect(ctx, x, barY, barWidth, Math.max(barHeight, 10), time, i);
    }
    
    // Draw ice breaking effect if bar is breaking free
    if (barState.breakTimer > 0) {
      drawIceBreakingEffect(ctx, x, barY, barWidth, Math.max(barHeight, 10), barState.breakTimer, time, i);
    }
    
    // Basic frost effect on top (enhanced when frozen)
    if (intensity > 0.3 || barState.frozen) {
      const frostAlpha = barState.frozen ? 0.9 : 0.6;
      const frostHeight = barState.frozen ? 5 : 3;
      ctx.fillStyle = `hsla(${hue}, 60%, 90%, ${frostAlpha})`;
      ctx.fillRect(x, barY, barWidth, frostHeight);
    }
    
    // Move to next position
    x += barWidth;
  }
  
  // Floating ice particles
  drawFloatingIceParticles(ctx, width, height, time);
  
  // Draw ice crystals falling from frozen bars
  drawFallingIceCrystals(ctx, width, height, time, barWidth);
}

function updateBarIceState(barIndex, intensity, deltaTime, barState) {
  const currentIceBuildup = iceBuildup.get(barIndex);
  
  // Ice buildup logic - gradually builds up when intensity is high
  if (intensity > 0.6 && !barState.frozen) {
    // Ice builds up faster with higher intensity
    const buildupRate = (intensity - 0.6) * 30 * deltaTime; // pixels per second
    iceBuildup.set(barIndex, Math.min(currentIceBuildup + buildupRate, 40)); // Max 40px buildup
  } else if (!barState.frozen) {
    // Ice melts slowly when intensity is low
    const meltRate = 10 * deltaTime; // pixels per second
    iceBuildup.set(barIndex, Math.max(currentIceBuildup - meltRate, 0));
  }
  
  // Random freezing logic - chance increases with ice buildup
  if (!barState.frozen && currentIceBuildup > 20) {
    const freezeChance = (currentIceBuildup / 40) * 0.002; // Higher buildup = higher chance
    if (Math.random() < freezeChance) {
      barState.frozen = true;
      barState.freezeTimer = 0;
      barState.breakTimer = 0;
      // Lock in the ice buildup when frozen
      iceBuildup.set(barIndex, currentIceBuildup + 10); // Add extra ice when freezing
    }
  }
  
  // Frozen bar logic
  if (barState.frozen) {
    barState.freezeTimer += deltaTime;
    
    // Random chance to start breaking free after being frozen for a while
    if (barState.freezeTimer > 2 && barState.breakTimer === 0) {
      const breakChance = Math.min(barState.freezeTimer - 2, 5) * 0.001; // Increases over time
      if (Math.random() < breakChance) {
        barState.breakTimer = 1.5; // 1.5 second break animation
      }
    }
    
    // Break timer countdown
    if (barState.breakTimer > 0) {
      barState.breakTimer -= deltaTime;
      if (barState.breakTimer <= 0) {
        // Bar breaks free!
        barState.frozen = false;
        barState.freezeTimer = 0;
        barState.breakTimer = 0;
        // Reduce ice buildup significantly when breaking free
        iceBuildup.set(barIndex, Math.max(currentIceBuildup * 0.3, 0));
      }
    }
  }
  
  frozenBars.set(barIndex, barState);
}

function drawIceBuildup(ctx, x, barY, barWidth, iceAmount, baseHue, time, barIndex) {
  if (iceAmount <= 0) return;
  
  // Draw layered ice buildup
  const layers = Math.floor(iceAmount / 8) + 1; // One layer per 8 pixels
  
  for (let layer = 0; layer < layers; layer++) {
    const layerHeight = Math.min(8, iceAmount - (layer * 8));
    if (layerHeight <= 0) break;
    
    const layerY = barY - (layer * 6); // Slightly overlapping layers
    const layerAlpha = 0.7 - (layer * 0.1); // Each layer slightly more transparent
    const layerHue = baseHue + (layer * 10); // Slight hue variation per layer
    
    // Jagged ice effect
    const jaggedOffset = Math.sin(time * 3 + barIndex + layer) * 2;
    
    ctx.fillStyle = `hsla(${layerHue}, 90%, 85%, ${layerAlpha})`;
    ctx.fillRect(x + jaggedOffset, layerY, barWidth - Math.abs(jaggedOffset), layerHeight);
    
    // Ice crystal highlights
    if (layer === 0) {
      ctx.fillStyle = `hsla(${layerHue}, 60%, 95%, 0.8)`;
      ctx.fillRect(x + jaggedOffset + 1, layerY, 2, layerHeight);
      ctx.fillRect(x + barWidth - 3 + jaggedOffset, layerY, 2, layerHeight);
    }
  }
}

function drawFrozenEffect(ctx, x, barY, barWidth, barHeight, time, barIndex) {
  // Frozen shimmer effect
  const shimmerAlpha = 0.3 + Math.sin(time * 4 + barIndex) * 0.2;
  ctx.fillStyle = `hsla(200, 100%, 90%, ${shimmerAlpha})`;
  ctx.fillRect(x, barY, barWidth, barHeight);
  
  // Ice crystal patterns
  ctx.strokeStyle = `hsla(180, 80%, 80%, 0.6)`;
  ctx.lineWidth = 1;
  
  // Vertical ice lines
  for (let i = 0; i < 3; i++) {
    const lineX = x + (barWidth / 4) * (i + 1);
    const lineOffset = Math.sin(time + barIndex + i) * 2;
    ctx.beginPath();
    ctx.moveTo(lineX + lineOffset, barY);
    ctx.lineTo(lineX + lineOffset, barY + barHeight);
    ctx.stroke();
  }
  
  // Horizontal ice cracks
  const crackY = barY + barHeight * 0.3;
  const crackOffset = Math.cos(time * 2 + barIndex) * 3;
  ctx.beginPath();
  ctx.moveTo(x, crackY + crackOffset);
  ctx.lineTo(x + barWidth, crackY + crackOffset);
  ctx.stroke();
}

function drawIceBreakingEffect(ctx, x, barY, barWidth, barHeight, breakTimer, time, barIndex) {
  // Breaking effect intensity increases as timer counts down
  const breakIntensity = 1 - (breakTimer / 1.5);
  
  // Flashing effect
  if (Math.sin(time * 20) > 0) {
    ctx.fillStyle = `hsla(60, 100%, 80%, ${breakIntensity * 0.5})`;
    ctx.fillRect(x, barY, barWidth, barHeight);
  }
  
  // Crack lines
  ctx.strokeStyle = `hsla(0, 0%, 100%, ${breakIntensity})`;
  ctx.lineWidth = 2;
  
  const numCracks = Math.floor(breakIntensity * 5) + 1;
  for (let i = 0; i < numCracks; i++) {
    const crackX = x + (barWidth / numCracks) * i + Math.sin(time * 10 + i) * 3;
    const crackStartY = barY + (barHeight * 0.2) * i;
    const crackEndY = barY + barHeight - (barHeight * 0.1) * i;
    
    ctx.beginPath();
    ctx.moveTo(crackX, crackStartY);
    ctx.lineTo(crackX + Math.cos(time * 15 + i) * 5, crackEndY);
    ctx.stroke();
  }
  
  // Ice shard particles
  if (breakIntensity > 0.7) {
    for (let i = 0; i < 8; i++) {
      const shardX = x + Math.random() * barWidth;
      const shardY = barY + Math.random() * barHeight;
      const shardSize = 1 + Math.random() * 2;
      
      ctx.fillStyle = `hsla(200, 80%, 90%, ${Math.random() * 0.8})`;
      ctx.beginPath();
      ctx.arc(shardX, shardY, shardSize, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

function drawFloatingIceParticles(ctx, width, height, time) {
  ctx.save();
  
  for (let i = 0; i < 15; i++) {
    const x = (Math.sin(time * 0.5 + i) * width * 0.3) + width * 0.5;
    const y = (Math.cos(time * 0.3 + i * 0.7) * height * 0.2) + height * 0.3;
    const size = 2 + Math.sin(time * 2 + i) * 1;
    
    ctx.fillStyle = `hsla(200, 80%, 80%, ${0.6 + Math.sin(time + i) * 0.3})`;
    ctx.beginPath();
    ctx.arc(x, y, size, 0, Math.PI * 2);
    ctx.fill();
  }
  
  ctx.restore();
}

function drawFallingIceCrystals(ctx, width, height, time, barWidth) {
  // Draw ice crystals falling from frozen bars
  frozenBars.forEach((barState, barIndex) => {
    if (barState.frozen) {
      const barX = barIndex * barWidth * 2.5 / (frozenBars.size / frozenBars.size);
      
      // Multiple crystals per frozen bar
      for (let i = 0; i < 3; i++) {
        const crystalX = barX + (barWidth * 0.2) + (i * barWidth * 0.3);
        const crystalY = (time * 50 + barIndex * 20 + i * 30) % (height + 20);
        const crystalSize = 1 + Math.sin(time * 3 + barIndex + i) * 0.5;
        
        if (crystalY < height) {
          ctx.fillStyle = `hsla(200, 90%, 85%, ${0.7 + Math.sin(time * 2 + i) * 0.2})`;
          ctx.beginPath();
          ctx.arc(crystalX, crystalY, crystalSize, 0, Math.PI * 2);
          ctx.fill();
          
          // Crystal sparkle
          if (Math.sin(time * 8 + barIndex + i) > 0.8) {
            ctx.fillStyle = `hsla(200, 100%, 95%, 0.9)`;
            ctx.beginPath();
            ctx.arc(crystalX, crystalY, crystalSize * 1.5, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
    }
  });
}

// Export the component function that matches the expected interface
export function FrostyWaves({ ctx, dataArray, width, height, isPlaying }) {
  const time = Date.now() * 0.001; // Convert to seconds for smooth animation
  drawFrostyWaves(ctx, dataArray, width, height, time);
}